/**
 * The sentence under each pre-season screen's title — spec v6b §3, ADR 9013.
 *
 * A rule per screen: a closed set of variants, each a catalogue key, chosen by a pure function
 * over the WHOLE read (every page `useApiCollection` collected), never over the rows drawn.
 * `headline.test.ts` renders every variant in the three launch locales.
 *
 * Also here, because the headline and the screen must agree on them: the summary figures each
 * screen's KPI band states (medians, sums, distinct counts). Money is summed only within one
 * currency; a buy priced in two has a count and no total.
 */

import type { BuyLineSummary, ClusterSchemeSummary, SizeCurveSummary } from '@/api/generated';
import { formatCompactNumber, displayNumberFormat } from '@/lib/format';
import { defineHeadline, sizeName, sizeNameAliased, type SizeName } from '@/planning';

import type { PlanFigures, PlanLedgerRow } from './ledger';
import { currentRefreshes, rawSharesOf } from './readers';

/** The platform's level code for a curve resolved at one door. */
export const STORE_LEVEL = 'store';

/** The median of a list, or `null` for none. */
export function median(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[mid] as number : ((sorted[mid - 1] as number) + (sorted[mid] as number)) / 2;
}

// ── size curves ──────────────────────────────────────────────────────────────

export interface SizeCorrection {
  readonly curve: SizeCurveSummary;
  /**
   * The size's KEY in `shares` — an opaque hash the curve read does not name (ADR 9027). An
   * identifier for lookups only; it is never put in front of a planner.
   */
  readonly size: string;
  /**
   * The size's 1-based place on the curve's axis (`curveSizes`): run order where the read names
   * its sizes, else the order the shares are published in. What an unnamed size is labelled by
   * ("Unnamed size 3").
   */
  readonly index: number;
  /** The size as the platform named it (`sizes`, bata-platform ADR 2006 as amended), or absent. */
  readonly name: SizeName;
  /** corrected − raw, as a share (0.03 = three share points). */
  readonly delta: number;
}

/**
 * The single largest correction across the article curves published: the size whose corrected
 * share moved furthest from its raw share. `null` when no curve carries a raw curve to compare
 * with — `rawShares` is optional on the contract, and a correction with no raw curve is not sized.
 *
 * Door curves are left out (contract audit B4): their stored raw mix was the ARTICLE's, so
 * «corrected − raw» on a door measured that door's difference from the chain, not a stockout
 * correction. The platform now publishes their raw map empty, which `rawSharesOf` reads as none;
 * the level test keeps the rule true against a platform that still sends the article's mix.
 */
export function largestCorrection(curves: readonly SizeCurveSummary[]): SizeCorrection | null {
  let best: SizeCorrection | null = null;
  for (const curve of curves) {
    if (curve.resolvedLevel === STORE_LEVEL) continue;
    const raw = rawSharesOf(curve);
    if (raw === undefined) continue;
    curveSizes(curve).forEach((name, position) => {
      const share = curve.shares[name.key];
      const before = raw[name.key];
      if (share === undefined || before === undefined) return;
      const delta = share - before;
      if (best === null || Math.abs(delta) > Math.abs(best.delta)) best = { curve, size: name.key, index: position + 1, name, delta };
    });
  }
  return best;
}

/**
 * A curve's sizes in axis order (bata-ui issue 470 consumption, ADR 9027): the read's `sizes` list
 * names each key of `shares` with its code, scale and run position — already in run order — or
 * says why it could not (`sizeAbsentReason`). A key of `shares` the list does not name follows,
 * absent as not published; a curve from a platform before the list keeps the shares' own order.
 * Never the key decoded. Pure.
 */
export function curveSizes(curve: Pick<SizeCurveSummary, 'shares' | 'sizes'>): readonly SizeName[] {
  return keyedSizes(Object.keys(curve.shares), curve.sizes);
}

/** The keys of a size-keyed map named from a published `sizes` list, in its order. Pure. */
export function keyedSizes(
  keys: readonly string[], sizes: SizeCurveSummary['sizes'],
): readonly SizeName[] {
  const present = new Set(keys);
  const named = (sizes ?? []).filter((size) => present.has(size.sizeKey)).map(sizeNameAliased);
  const seen = new Set(named.map((name) => name.key));
  return [...named, ...keys.filter((key) => !seen.has(key)).map((key) => sizeName({ size_key: key }))];
}

/** A share difference in share points, signed, one decimal. */
export function sharePoints(delta: number, locale: string): string {
  return displayNumberFormat(locale, { maximumFractionDigits: 1, minimumFractionDigits: 1, signDisplay: 'exceptZero' })
    .format(delta * 100);
}

export const sizeCurveHeadline = defineHeadline({
  keys: {
    empty: 'preseason.headline.sizeCurve.empty',
    corrected: 'preseason.headline.sizeCurve.corrected',
    correctedNamed: 'preseason.headline.sizeCurve.correctedNamed',
    noRaw: 'preseason.headline.sizeCurve.noRaw',
  },
  choose: (curves: readonly SizeCurveSummary[], { locale }) => {
    if (curves.length === 0) return { variant: 'empty' };
    const largest = largestCorrection(curves);
    if (largest === null) return { variant: 'noRaw', values: { count: curves.length } };
    const points = sharePoints(largest.delta, locale);
    return largest.name.code === null
      ? { variant: 'corrected', values: { count: curves.length, size: String(largest.index), points } }
      : { variant: 'correctedNamed', values: { count: curves.length, size: largest.name.code, points } };
  },
});

// ── clusters ─────────────────────────────────────────────────────────────────

/**
 * The cluster page's figures, over the CURRENT refresh of each scheme only.
 *
 * Every weekly refresh of a scheme is its own row (`r0, r1, …`), and those rows are the scheme's
 * history, not schemes that coexist. Adding them up made «Clusters» grow every Monday with no
 * change in the estate (contract audit M2), so the figures read one row per scheme — the row
 * `currentRefreshes` names — and the older refreshes are counted apart, as history.
 */
export interface ClusterFigures {
  /** Schemes with a current refresh — one per scheme. */
  readonly schemes: number;
  /** Clusters in the scheme shown (the page's chosen scheme version), never a sum across refreshes. */
  readonly clustersShown: number | null;
  /** The widest current scheme's door count — schemes coexist over the same doors, so a sum double-counts. */
  readonly widestStores: number;
  /** Current refreshes that are frozen. */
  readonly frozen: number;
  /** Rows that are an older refresh of a scheme — history, not a scheme. */
  readonly previousRefreshes: number;
}

export function clusterFigures(
  schemes: readonly ClusterSchemeSummary[],
  shown: ClusterSchemeSummary | undefined = undefined,
): ClusterFigures | null {
  const current = currentRefreshes(schemes);
  if (current.length === 0) return null;
  return {
    schemes: current.length,
    clustersShown: shown === undefined ? null : shown.clusterCount,
    widestStores: Math.max(...current.map((scheme) => scheme.storeCount)),
    frozen: current.filter((scheme) => scheme.frozen).length,
    previousRefreshes: schemes.length - current.length,
  };
}

export const clusterHeadline = defineHeadline({
  keys: {
    empty: 'preseason.headline.cluster.empty',
    schemes: 'preseason.headline.cluster.current',
  },
  choose: (schemes: readonly ClusterSchemeSummary[]) => {
    const figures = clusterFigures(schemes);
    return figures === null
      ? { variant: 'empty' }
      : {
        variant: 'schemes',
        values: { schemes: figures.schemes, frozen: figures.frozen, previous: figures.previousRefreshes },
      };
  },
});

// ── open-to-buy plan ─────────────────────────────────────────────────────────

export const otbHeadline = defineHeadline({
  keys: {
    empty: 'preseason.headline.otb.empty',
    oneNode: 'preseason.headline.otb.oneNode',
    severalNodes: 'preseason.headline.otb.severalNodes',
    calculating: 'preseason.headline.otb.calculating',
  },
  choose: ({ rows, figures }: { readonly rows: readonly PlanLedgerRow[]; readonly figures: PlanFigures | null }, { locale }) => {
    if (figures === null) return { variant: 'empty' };
    if (rows.every((row) => row.openToBuyUnits === null)) return { variant: 'calculating', values: { lines: figures.lines } };
    if (figures.totalOpenToBuy !== null) {
      return {
        variant: 'oneNode',
        values: { units: formatCompactNumber(figures.totalOpenToBuy, locale).text, periods: figures.lines, over: figures.overCommitted },
      };
    }
    return { variant: 'severalNodes', values: { nodes: figures.nodes, lines: figures.lines, over: figures.overCommitted } };
  },
});

// ── buy ──────────────────────────────────────────────────────────────────────

export interface BuyFigures {
  readonly lines: number;
  readonly units: number;
  readonly suppliers: number;
  readonly periods: number;
  /** Σ quantity × unit cost, only when every line is in one currency. */
  readonly value: { readonly amount: number; readonly currency: string } | null;
  readonly currencies: number;
}

export function buyFigures(lines: readonly BuyLineSummary[]): BuyFigures | null {
  if (lines.length === 0) return null;
  const currencies = new Set(lines.map((line) => line.currencyCode));
  return {
    lines: lines.length,
    units: lines.reduce((sum, line) => sum + line.quantityUnits, 0),
    suppliers: new Set(lines.map((line) => line.supplierKey)).size,
    periods: new Set(lines.map((line) => line.receiptPeriodKey)).size,
    value: currencies.size === 1
      ? { amount: lines.reduce((sum, line) => sum + line.quantityUnits * line.unitCostAmount, 0), currency: [...currencies][0] as string }
      : null,
    currencies: currencies.size,
  };
}

export const buyHeadline = defineHeadline({
  keys: {
    empty: 'preseason.headline.buy.empty',
    oneCurrency: 'preseason.headline.buy.oneCurrency',
    multiCurrency: 'preseason.headline.buy.multiCurrency',
  },
  choose: (lines: readonly BuyLineSummary[], { locale }) => {
    const figures = buyFigures(lines);
    if (figures === null) return { variant: 'empty' };
    const units = formatCompactNumber(figures.units, locale).text;
    return figures.value === null
      ? { variant: 'multiCurrency', values: { lines: figures.lines, units, suppliers: figures.suppliers, currencies: figures.currencies } }
      : {
        variant: 'oneCurrency',
        values: {
          lines: figures.lines, units, suppliers: figures.suppliers,
          value: formatCompactNumber(figures.value.amount, locale).text, currency: figures.value.currency,
        },
      };
  },
});
